<script setup>
  import { useSettingsStore } from '@stores/settings';
  import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
  import { ref, watch, computed, nextTick, onMounted } from 'vue';
  import Carousel from '@/components/ui/carousel/Carousel.vue';
  import CarouselContent from '@/components/ui/carousel/CarouselContent.vue';
  import CarouselItem from '@/components/ui/carousel/CarouselItem.vue';
  import CarouselNext from '@/components/ui/carousel/CarouselNext.vue';
  import CarouselPrevious from '@/components/ui/carousel/CarouselPrevious.vue';

  const route = useRoute();
  const settingsStore = useSettingsStore();
  const settings = settingsStore.settings;

  const carouselApi = ref(null);
  const menuItems = computed(() => [
    { title: 'Perfil', to: '/user/account', icon: 'mdi:user' },
    { title: 'Carteira', to: '/user/wallet', icon: 'mingcute:wallet-4-fill' },
    { title: 'Promoções', to: '/user/promotion', icon: 'mdi:gift' },
    ...(settings.deposit_enable_popup
      ? []
      : [{ title: 'Depósito', to: '/user/wallet/deposit', icon: 'mdi:piggy-bank' }]),

    { title: 'Proteção', to: '/user/protection', icon: 'fa6-solid:shield-heart' }
  ]);

  const VISIBLE_ROUTES = [
    'user-coupon',
    'user-bonus',
    'user-account',
    'user-promotion',
    'user-promotion-coupon',
    'user-account-document',
    'user-transaction',
    'user-account-password',
    'user-wallet',
    'user-wallet-withdraw',
    'user-wallet-deposit',
    'user-deposit-id',
    'user-protection',
    'user-protection-suspension',
    'user-protection-exclusion',
    'user-protection-financercontroller',
    'user-protection-permcontroller'
  ];

  const scrollToActiveItem = async () => {
    const activeItemIndex = menuItems.value.findIndex((item) => item.to === route.fullPath);

    if (activeItemIndex !== -1 && carouselApi.value) {
      carouselApi.value.scrollTo(activeItemIndex);
      await nextTick();

      const carouselItems = document.querySelectorAll('.carousel-item');
      const activeCarouselItem = carouselItems[activeItemIndex];
      if (activeCarouselItem) {
        const button = activeCarouselItem.querySelector('button');
        if (button) {
          button.focus({ preventScroll: true });
        }
      }
    }
  };

  watch(
    () => route.fullPath,
    () => {
      scrollToActiveItem();
    }
  );

  onMounted(() => {
    scrollToActiveItem();
  });
</script>

<template>
  <div
    class="w-full max-w-6xl flex-col justify-between px-4 lg:px-10 sm:flex sm:flex-row sm:px-6 mt-5 h-fit"
    :class="VISIBLE_ROUTES.includes(route.name) ? 'block' : 'hidden'"
  >
    <div class="w-full max-w-6xl mx-auto relative">
      <Carousel
        @init-api="(api) => (carouselApi = api)"
        :opts="{
          align: 'center',
          dragFree: true,
          skipSnaps: true,
          containScroll: 'trimSnaps',
          loop: false
        }"
      >
        <CarouselContent class="lg:px-1 lg:mr-0 mr-0 carousel-content">
          <CarouselItem
            v-for="item in menuItems"
            :key="item.to"
            class="basis-[40%] xs:basis-[44.5%] md:basis-[22%] lg:basis-1/4 lg:text-base text-xs carousel-item"
          >
            <BaseButton
              class="w-full py-2 rounded-md hover:border-brand-primary focus-visible:outline-none transition-colors duration-200 flex items-center gap-2"
              :to="item.to"
              :class="
                route.fullPath.startsWith(item.to)
                  ? '!border-brand-primary text-brand-primary'
                  : 'border-white/10'
              "
            >
              <Icon :name="item.icon" size="20" />
              <span class="text-xs lg:text-base">{{ item.title }}</span>
            </BaseButton>
          </CarouselItem>
        </CarouselContent>

        <!-- Botão Anterior -->
        <CarouselPrevious class="hidden lg:flex absolute -left-7 top-1/2 -translate-y-1/2">
          <ChevronLeft class="w-6 h-6" />
        </CarouselPrevious>

        <!-- Botão Próximo -->
        <CarouselNext class="hidden lg:flex absolute -right-7 top-1/2 -translate-y-1/2">
          <ChevronRight class="w-6 h-6" />
        </CarouselNext>
      </Carousel>
    </div>
  </div>
</template>
